import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Modal, Form, Table, Badge, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosClient from '../../services/axiosClient';
import './Ads.css';

const Ads = () => {
  const [ads, setAds] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAd, setCurrentAd] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  const [formData, setFormData] = useState({
    company_id: '',
    title: '',
    description: '',
    image_url: '',
    cta_label: '',
    cta_url: '',
    tags: '',
    starts_at: '',
    ends_at: '',
    active: true
  });

  // Charger les publicités
  const fetchAds = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get('/admin/ads');
      setAds(response.data.data || response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des publicités:', error);
      toast.error('Erreur lors du chargement des publicités');
    } finally {
      setLoading(false);
    }
  };

  // Charger les entreprises avec abonnement actif
  const fetchSubscribedCompanies = async () => {
    try {
      const response = await axiosClient.get('/admin/companies/subscribed');
      setCompanies(response.data.data || response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des entreprises:', error);
      toast.error('Erreur lors du chargement des entreprises');
    }
  };

  useEffect(() => {
    fetchAds();
    fetchSubscribedCompanies();
  }, []);

  // Gérer le changement de fichier image
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Ouvrir le modal pour créer
  const handleCreate = () => {
    setEditMode(false);
    setCurrentAd(null);
    setImageFile(null);
    setImagePreview(null);
    setFormData({
      company_id: '',
      title: '',
      description: '',
      image_url: '',
      cta_label: '',
      cta_url: '',
      tags: '',
      starts_at: '',
      ends_at: '',
      active: true
    });
    setShowModal(true);
  };

  // Ouvrir le modal pour éditer
  const handleEdit = (ad) => {
    setEditMode(true);
    setCurrentAd(ad);
    setImageFile(null);
    setImagePreview(ad.image_url);
    setFormData({
      company_id: ad.company_id || '',
      title: ad.title || '',
      description: ad.description || '',
      image_url: ad.image_url || '',
      cta_label: ad.cta_label || '',
      cta_url: ad.cta_url || '',
      tags: Array.isArray(ad.tags) ? ad.tags.join(', ') : '',
      starts_at: ad.starts_at ? ad.starts_at.split('T')[0] : '',
      ends_at: ad.ends_at ? ad.ends_at.split('T')[0] : '',
      active: ad.active || false
    });
    setShowModal(true);
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();
      
      // Ajouter les champs
      formDataToSend.append('company_id', formData.company_id);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('cta_label', formData.cta_label);
      formDataToSend.append('cta_url', formData.cta_url);
      formDataToSend.append('active', formData.active ? '1' : '0');
      
      if (formData.starts_at) formDataToSend.append('starts_at', formData.starts_at);
      if (formData.ends_at) formDataToSend.append('ends_at', formData.ends_at);
      
      // Ajouter les tags
      if (formData.tags) {
        const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
        tagsArray.forEach(tag => {
          formDataToSend.append('tags[]', tag);
        });
      }
      
      // Ajouter l'image si elle existe
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      } else if (formData.image_url && !editMode) {
        formDataToSend.append('image_url', formData.image_url);
      }

      if (editMode) {
        // Mise à jour
        await axiosClient.post(`/admin/ads/${currentAd.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Publicité mise à jour avec succès');
      } else {
        // Création
        await axiosClient.post('/admin/ads', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Publicité créée avec succès');
      }

      setShowModal(false);
      fetchAds();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  // Supprimer une publicité
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Êtes-vous sûr ?',
      text: "Cette action est irréversible !",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer !',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await axiosClient.delete(`/admin/ads/${id}`);
        toast.success('Publicité supprimée avec succès');
        fetchAds();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  // Basculer le statut actif/inactif
  const toggleActive = async (ad) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('active', ad.active ? '0' : '1');
      
      await axiosClient.post(`/admin/ads/${ad.id}`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Publicité ${ad.active ? 'désactivée' : 'activée'} avec succès`);
      fetchAds();
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      toast.error('Erreur lors du changement de statut');
    }
  };

  return (
    <div className="ads-management">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <Row className="mb-4">
        <Col>
          <h2>Gestion des Publicités</h2>
          <p className="text-muted">Gérez les publicités affichées sur le site</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleCreate}>
            <i className="fas fa-plus me-2"></i>
            Nouvelle Publicité
          </Button>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Chargement...</p>
            </div>
          ) : ads.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-ad fa-3x text-muted mb-3"></i>
              <p className="text-muted">Aucune publicité trouvée</p>
              <Button variant="primary" onClick={handleCreate}>
                Créer la première publicité
              </Button>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Titre</th>
                  <th>Entreprise</th>
                  <th>Période</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ads.map((ad) => (
                  <tr key={ad.id}>
                    <td>
                      {ad.image_url ? (
                        <img 
                          src={ad.image_url} 
                          alt={ad.title}
                          style={{ width: '80px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                      ) : (
                        <div style={{ width: '80px', height: '50px', background: '#f0f0f0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="fas fa-image text-muted"></i>
                        </div>
                      )}
                    </td>
                    <td>
                      <strong>{ad.title}</strong>
                      <br />
                      <small className="text-muted">{ad.description?.substring(0, 50)}...</small>
                    </td>
                    <td>{ad.company?.name || 'N/A'}</td>
                    <td>
                      <small>
                        {ad.starts_at && <div>Début: {new Date(ad.starts_at).toLocaleDateString()}</div>}
                        {ad.ends_at && <div>Fin: {new Date(ad.ends_at).toLocaleDateString()}</div>}
                      </small>
                    </td>
                    <td>
                      <Badge 
                        bg={ad.active ? 'success' : 'secondary'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleActive(ad)}
                      >
                        {ad.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleEdit(ad)}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDelete(ad.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal Créer/Éditer */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? 'Modifier la Publicité' : 'Nouvelle Publicité'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Entreprise *</Form.Label>
                  <Form.Select
                    value={formData.company_id}
                    onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                    required
                  >
                    <option value="">Sélectionner une entreprise</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Seules les entreprises avec abonnement actif
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Titre *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    placeholder="Ex: Boostez votre visibilité"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description de la publicité"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Image</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  <Form.Text className="text-muted">
                    Ou entrez une URL d'image ci-dessous
                  </Form.Text>
                </Form.Group>
                {imagePreview && (
                  <div className="mb-3">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                  </div>
                )}
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>URL de l'image</Form.Label>
                  <Form.Control
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Label du bouton</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.cta_label}
                    onChange={(e) => setFormData({ ...formData, cta_label: e.target.value })}
                    placeholder="Ex: En savoir plus"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>URL du bouton</Form.Label>
                  <Form.Control
                    type="url"
                    value={formData.cta_url}
                    onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
                    placeholder="https://example.com"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <Form.Control
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="Ciblage, Performance, Branding (séparés par des virgules)"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date de début</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.starts_at}
                    onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date de fin</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.ends_at}
                    onChange={(e) => setFormData({ ...formData, ends_at: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="active-switch"
                label="Publicité active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit">
              {editMode ? 'Mettre à jour' : 'Créer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Ads;
